import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ArrowUpRight, List, X } from "@phosphor-icons/react";

const navItems = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "About", to: "/about" },
  { label: "Impact", to: "/impact" },
  { label: "Insights", to: "/insights" },
  { label: "Contact Us", to: "/contact#direct-lines" },
];

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-3 group" data-testid="site-logo">
      <span className="relative flex items-center justify-center w-8 h-8 border border-white/20 group-hover:border-[#00F0FF] transition-colors">
        {/* glowing inner block */}
        <span className="absolute inset-1 bg-[#00F0FF] shadow-[0_0_18px_2px_rgba(0,240,255,0.55)]" />
        {/* pulsing dot at corner */}
        <span className="absolute -right-1 -bottom-1 w-2 h-2 bg-[#00F0FF] pulse-dot" />
      </span>
      <span className="flex flex-col leading-none">
        <span className="font-display text-base md:text-lg font-black tracking-tight text-white">
          HUMINT<span className="text-cy">.</span>
        </span>
        <span className="font-mono text-[9px] md:text-[10px] tracking-[0.28em] text-zinc-400 mt-1">
          HUMAN INTELLIGENCE
        </span>
      </span>
    </Link>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  if (location.pathname.startsWith("/admin")) return null;

  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 bg-[#050505]/85 backdrop-blur-xl border-b border-ink-600"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 h-[72px] flex items-center justify-between">
        <Logo />

        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative text-sm font-medium transition-colors pb-1 ${
                  isActive ? "text-white" : "text-zinc-400 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <span className="relative inline-block">
                  {n.label}
                  {isActive && (
                    <span className="absolute left-0 -bottom-1 h-px w-full bg-cy" />
                  )}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <Link
          to="/contact"
          data-testid="nav-cta"
          className="hidden md:inline-flex items-center gap-2 bg-cy text-ink px-5 py-2.5 text-sm font-semibold hover:bg-white transition-colors group"
        >
          Start Diagnosis
          <ArrowUpRight
            size={16}
            weight="bold"
            className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
          />
        </Link>

        <button
          onClick={() => setOpen(!open)}
          className="md:hidden p-2 text-white"
          aria-label="Toggle menu"
          data-testid="nav-toggle"
        >
          {open ? <X size={22} /> : <List size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#050505] border-t border-ink-600 px-6 py-6 space-y-4">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block text-base ${isActive ? "text-cy" : "text-zinc-300"}`
              }
              data-testid={`nav-mobile-${n.label.toLowerCase()}`}
            >
              {n.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            onClick={() => setOpen(false)}
            className="block bg-cy text-ink text-center py-3 text-sm font-semibold"
            data-testid="nav-cta-mobile"
          >
            Start Diagnosis
          </Link>
        </div>
      )}
    </header>
  );
}
